import { createContext, useContext, ReactNode } from 'react';
import { ComponentKey } from 'enums/componentKey';
import { InputRenderers, RowInputRenderers } from './types';

/* The registry comes through context: the composition root (src/index.tsx)
   assembles core + feature renderers and mounts the provider once — the
   factory never imports a feature. Two tables: form fields (bound to Formik
   by name) and the controlled sub-fields of DynamicArray rows. */
export interface InputRegistry {
  renderers: InputRenderers;
  rowRenderers: RowInputRenderers;
}

const InputRegistryContext = createContext<InputRegistry | null>(null);

interface ProviderProps extends InputRegistry {
  children: ReactNode;
}

export const InputRenderersProvider = ({ renderers, rowRenderers, children }: ProviderProps) => (
  <InputRegistryContext.Provider value={{ renderers, rowRenderers }}>
    {children}
  </InputRegistryContext.Provider>
);

export const useInputRegistry = (): InputRegistry => {
  const registry = useContext(InputRegistryContext);
  if (!registry) {
    throw new Error('InputFactory / DynamicArray must be rendered under <InputRenderersProvider>');
  }
  return registry;
};

/* The registry types are complete, so a missing renderer can only come from
   an untyped config. Warn once per key, by name (ComponentKey is a numeric enum). */
const warnedKeys = new Set<ComponentKey>();

export const warnMissingRenderer = (key: ComponentKey, fieldName: string) => {
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn(`No input renderer registered for ComponentKey.${ComponentKey[key]} (field "${fieldName}")`);
};
